/**
 * Centralized color mode utilities
 * Determines whether a light is in color mode (hue/sat) vs white mode (color temperature)
 */

import type { Light } from '@/stores/lights'
import type { Scene } from '@/stores/scenes'
import type { SceneLightConfig } from '@/stores/sceneLightConfigs'

export interface ColorModeContext {
  currentSceneId: string | null
  currentScene?: Scene | null
  getConfig: (sceneId: string, lightId: string) => SceneLightConfig | undefined
}

/**
 * Check if a light is in color mode (hue/saturation) vs white mode (color temperature)
 * 
 * Priority:
 * 1. Scene config's target_state (if scene is selected)
 * 2. Parent scene config's target_state (if inherited)
 * 3. Light's current_state (fallback)
 * 
 * A light is in color mode if it has NO color_temperature set
 */
export function isLightInColorMode(light: Light, context: ColorModeContext): boolean {
  if (!context.currentSceneId) {
    // Fallback to current_state if no scene selected
    const state = light.current_state
    return state?.ct == null
  }

  // Check the scene config's target_state, not the light's current_state
  const config = context.getConfig(context.currentSceneId, light.id)

  if (!config && context.currentScene?.parent_id) {
    // Check parent config if no direct config
    const parentConfig = context.getConfig(context.currentScene.parent_id, light.id)
    if (parentConfig) {
      // Check parent's target state
      return parentConfig.target_state.color_temperature == null
    }
  }

  if (config) {
    // Has config - check target_state
    // If color_temperature exists in target_state, it's white mode
    return config.target_state.color_temperature == null
  }

  // No config at all - fallback to current state
  const state = light.current_state
  return state?.ct == null
}

/**
 * Inverse of isLightInColorMode
 */
export function isLightInWhiteMode(light: Light, context: ColorModeContext): boolean {
  return !isLightInColorMode(light, context)
}

/**
 * Get the color mode as a string
 */
export function getLightColorMode(light: Light, context: ColorModeContext): 'color' | 'white' {
  return isLightInColorMode(light, context) ? 'color' : 'white'
}
